import { t } from 'i18next';
import close from '../../../assets/img/svg//table/close.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutsideFocus } from '../../../hook/useOutsideFocus';
import { SlideDirection } from '../../../enum/SlideDirection';
import { CashRegisterItem } from '../../../interface/response/CashRegisterDataResponse';
import { ANIMATION_DURATION_MS, DECIMAL_NUMBERS_REGEX } from '../../../constant/constants';
import update from '../../../assets/img/svg/buttons/update-check.svg';
import cancel from '../../../assets/img/svg/buttons/cancel.svg';
import { postCashRegisterPayInPayOut } from '../../../store/slice/cash-register-slice';
import { PayInPayOut } from '../../../enum/PayInPayOut';
import { useAppDispatch } from '../../../hook/useAppDispatch';
import { TransactionType } from '../../../enum/TransactionType';

interface CashRegisterPayInPayOutProps {
  onClose: () => void;
  selectedItem: CashRegisterItem | null;
}
const CashRegisterPayInPayOut: React.FC<CashRegisterPayInPayOutProps> = ({ onClose, selectedItem }) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [validationMessage, setValidationMessage] = useState('');
  const [slideState, setSlideState] = useState<
    SlideDirection.IN | SlideDirection.OUT_LEFT | SlideDirection.OUT_RIGHT | SlideDirection.NONE
  >(SlideDirection.NONE);

  useEffect(() => {
    setSlideState(SlideDirection.IN);
  }, []);

  const handleClose = useCallback(() => {
    setSlideState(SlideDirection.OUT_RIGHT);

    setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION_MS);
  }, [onClose]);

  useOutsideFocus(true, handleClose, ref);
  const [type, setType] = useState<number>();
  const [value, setValue] = useState('0.00');

  const handleChange = (e: any) => {
    const val = e.target.value;

    if (val === '' || DECIMAL_NUMBERS_REGEX.test(val)) {
      setValue(val);
    }
  };

  const handleBlur = () => {
    if (value === '' || isNaN(Number(value))) {
      setValue('0.00');
    } else {
      setValue(Number(value).toFixed(2));
    }
  };

  const increase = () => {
    const num = Number(value || 0);
    setValue((num + 1).toFixed(2));
  };

  const decrease = () => {
    const num = Number(value || 0);
    setValue(Math.max(0, num - 1).toFixed(2));
  };

  const handleUpdate = async () => {
    if (!selectedItem) return;

    if (type === undefined) {
      setValidationMessage(t('cashRegister.table.payInPayOut.payInPayOutValidationMessage'));
      return;
    }
    const payload = {
      LocationID: selectedItem.LocationID,
      DeviceID: selectedItem.DeviceID,
      Amount: Number(value) * 100,
      Type: type,
      TransactionType: TransactionType.TRANSACTION_TYPE,
    };
    await dispatch(postCashRegisterPayInPayOut(payload));
    handleClose();
  };

  return (
    <div
      className={`cash_register_payin_payout 
    ${slideState === SlideDirection.IN ? 'slide_in' : ''} 
    ${slideState === SlideDirection.OUT_LEFT ? 'slide_out' : ''} 
    ${slideState === SlideDirection.OUT_RIGHT ? 'slide_out_right' : ''}`}
      ref={ref}
    >
      <header className="header">
        <span className="title">{t('cashRegister.table.payInPayOut.edit')}</span>

        <img className="close" src={close} alt="close" onClick={handleClose} />
      </header>
      <hr />
      <div className="row">
        <span className="label">{t('cashRegister.table.payInPayOut.location')}</span>

        <span className="value">{selectedItem?.LocationName}</span>
      </div>

      <div className="row">
        <span className="label">{t('cashRegister.table.payInPayOut.device')}</span>

        <span className="value">
          {selectedItem?.DeviceName === null ? t('cashRegister.table.cellValue.perLocation') : selectedItem?.DeviceName}
        </span>
      </div>

      <div className="row">
        <span className="label">{t('cashRegister.table.payInPayOut.state')}</span>

        <span className="value">{selectedItem?.EndState}</span>
      </div>

      <div className="row">
        <span className="label">{t('cashRegister.table.payInPayOut.currency')}</span>

        <span className="value">{selectedItem?.Currency}</span>
      </div>

      <div className="row radio_row">
        <span className="label">
          {t('cashRegister.table.payInPayOut.payIn')}/{t('cashRegister.table.payInPayOut.payOut')}
        </span>
        <div className="radio_buttons_container">
          <div>
            <label>
              <input
                type="radio"
                name="payIn"
                value={PayInPayOut.PAYIN}
                checked={type === PayInPayOut.PAYIN}
                onChange={() => {
                  setType(PayInPayOut.PAYIN);
                  setValidationMessage('');
                }}
              />
              {t('cashRegister.table.payInPayOut.payIn')}
            </label>
          </div>

          <div>
            <label>
              <input
                type="radio"
                name="payOut"
                value={PayInPayOut.PAYOUT}
                checked={type === PayInPayOut.PAYOUT}
                onChange={() => {
                  setType(PayInPayOut.PAYOUT);
                  setValidationMessage('');
                }}
              />
              {t('cashRegister.table.payInPayOut.payOut')}
            </label>
          </div>
          {validationMessage && (
            <div className="validation_message">
              <span className="validation_icon">⚠</span>
              <span className="validation_text">{validationMessage}</span>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <span className="label">{t('cashRegister.table.payInPayOut.amount')}</span>

        <div className="number_input_wrapper value">
          <input
            className="value input"
            type="text"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <div className="number_input_stepper">
            <button type="button" onClick={increase}>
              ▲
            </button>
            <button type="button" onClick={decrease}>
              ▼
            </button>
          </div>
        </div>
      </div>
      <div className="row actions">
        <button className="update" onClick={() => handleUpdate()}>
          <img src={update} alt="Update" /> Update
        </button>
        <button className="cancel" onClick={handleClose}>
          <img src={cancel} alt="Update" /> Cancel
        </button>
      </div>
    </div>
  );
};

export default CashRegisterPayInPayOut;
